"use client";
import { ICartItemType } from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import { ICommonProductDetails } from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import { currencyAtom } from "@/hooks/currencyAtom";

import { getCartItemFromProductToEvent } from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/functions";
import { EventType } from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/interfaces";
import useEventTrackers from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";
import { useAtom, useSetAtom } from "jotai";
import React, { useEffect } from "react";
import { latestViewedProductsAtom } from "@/hooks/productAtoms";
import { IProduct } from "@/api/AppApi/services/home/interfaces/IProduct";
import { useCacheInvalidation } from "@/hooks/useCacheInvalidation";

const ViewProductPixel = ({
  product,
  eventId,
}: {
  product: ICommonProductDetails;
  eventId?: string;
}) => {
  useCacheInvalidation();
  const { eventTrackers } = useEventTrackers();
  const [currency] = useAtom(currencyAtom);
  const handleTimerEnd = React.useCallback(() => {
    eventTrackers(
      EventType.VIEW_CONTENT,
      [
        {
          ...(getCartItemFromProductToEvent({
            parent: { name: product.name, sku: product.sku },
            inner_id: product.id,
            discount: product.discount,
            name: product.name,
            type: product.preview_type,
            quantity: 1,
            price: product.price,
            categories_names: product?.categories_names ?? [],
            attrs_values: product.attributes_builder.map((attr) => ({
              name: attr.configurations.title,
            })),
          }) as unknown as ICartItemType),
        },
      ],
      {
        value: product?.price,
        currency: currency?.code,
        discount: product?.discount,
      },
      undefined,
      eventId,
    );
  }, [product, currency, eventTrackers, eventId]);

  const setLatestViewed = useSetAtom(latestViewedProductsAtom);

  useEffect(() => {
    if (!product) return;
    let time = 1;
    const timerId = setInterval(() => {
      if (time === 0) {
        clearInterval(timerId);
        handleTimerEnd();
        setLatestViewed((prev) => {
          const newProduct = product as unknown as IProduct;
          const filtered = prev.filter((p) => p.id !== newProduct.id);
          return [newProduct, ...filtered].slice(0, 10);
        });
      } else {
        time -= 1;
      }
    }, 500);

    return () => clearInterval(timerId);
  }, [product, handleTimerEnd, setLatestViewed]);

  return <></>;
};

export default ViewProductPixel;
